/**
 * Recently Viewed Products
 * Fetches product IDs from localStorage and renders them dynamically
 * Uses Shopify's recommendations API to fetch product data
 */

(function() {
  'use strict';

  function getProductTypeLabel(productType) {
    if (productType === 'OMR Academy' || productType === 'OMR Academy Kompakt') {
      return 'E-Learning-Kurs';
    } else if (productType === 'OMR Report') {
      return 'Praxis-Guide';
    } else if (productType === 'OMR Deep Dive' || productType === 'OMR Deep Dive Einzelsession') {
      return 'Online-Seminar';
    }
    return productType;
  }

  function createProductCard(product, index) {
    const isFeatured = index % 3 === 0;
    const featuredClass = isFeatured ? 'collection-recently-viewed__product-card--featured' : '';

    // Get product image - try multiple sources
    // Shopify products.json API returns featured_image or images array
    let productImage = product.featured_image || product.featuredImage || null;

    // If no featured_image, try first image from images array
    if (!productImage && product.images && product.images.length > 0) {
      productImage = product.images[0];
    }

    // If still no image, try featured_media (though this might not be in JSON API)
    if (!productImage && product.featured_media) {
      if (typeof product.featured_media === 'string') {
        productImage = product.featured_media;
      } else if (product.featured_media.preview_image) {
        productImage = product.featured_media.preview_image.src || product.featured_media.preview_image;
      }
    }

    const productUrl = product.url || `/products/${product.handle}`;
    let productTitle = product.title || product.name || '';
    // Remove everything before "|" if it exists
    if (productTitle.includes('|')) {
      productTitle = productTitle.split('|').slice(1).join('|').trim();
    }
    const productType = product.type || product.product_type || '';
    const typeLabel = getProductTypeLabel(productType);

    // Process image URL with appropriate width based on card type
    let imageSrc = null;
    if (productImage) {
      // Handle both string URLs and object with src property
      let imageUrl = productImage;
      if (typeof productImage === 'object' && productImage.src) {
        imageUrl = productImage.src;
      }

      // Ensure HTTPS protocol
      if (!imageUrl.startsWith('http')) {
        imageUrl = 'https:' + imageUrl;
      }

      // Determine width parameter based on card type
      // Featured cards: 240px * 2 = 480px for retina displays
      // Regular cards: 173px * 2 = 346px for retina displays
      const width = isFeatured ? 480 : 346;

      // Replace or add width parameter
      if (imageUrl.includes('?')) {
        // Remove existing width parameter if present
        imageUrl = imageUrl.replace(/[?&]width=\d+/g, '');
        imageSrc = imageUrl + '&width=' + width;
      } else {
        // Add width parameter
        imageSrc = imageUrl + '?width=' + width;
      }
    }

    let cardHTML = `
      <a href="${productUrl}" class="collection-recently-viewed__product-card ${featuredClass}">
    `;

    // All cards use the same square wrapper
    cardHTML += `
      <div class="collection-recently-viewed__card-image">
    `;

    if (imageSrc) {
      cardHTML += `
        <img
          src="${imageSrc}"
          alt="${productTitle}"
          width="240"
          height="240"
          loading="lazy"
        >
      `;
    } else {
      cardHTML += `
        <div class="collection-recently-viewed__card-placeholder"></div>
      `;
    }

    cardHTML += `
      </div>
    `;

    cardHTML += `
        <p class="collection-recently-viewed__product-info">
          <span class="collection-recently-viewed__product-title">${productTitle}</span>
          <span class="collection-recently-viewed__product-type"> · ${typeLabel}</span>
        </p>
      </a>
    `;

    return cardHTML;
  }

  function fetchProductsByHandles(productHandles) {
    if (!productHandles || productHandles.length === 0) {
      return Promise.resolve([]);
    }

    // Fetch products by handle using the products.json endpoint
    const fetchPromises = productHandles.map(handle => {
      const url = `/products/${handle}.json`;
      return fetch(url)
        .then(response => {
          if (!response.ok) {
            console.warn('Failed to fetch product:', url, response.status);
            return null;
          }
          return response.json();
        })
        .then(data => {
          const product = data.product || null;
          if (product) {
          }
          return product;
        })
        .catch(error => {
          console.error('Error fetching product by handle:', handle, error);
          return null;
        });
    });

    return Promise.all(fetchPromises).then(products => {
      // Filter out null values (failed fetches)
      const validProducts = products.filter(p => p !== null && p !== undefined);
      return validProducts;
    }).catch(error => {
      console.error('Error fetching products by handles:', error);
      return [];
    });
  }

  function fetchProductsByIds(productIds) {
    if (!productIds || productIds.length === 0) {
      return Promise.resolve([]);
    }

    // Fetch products using recommendations API
    // For each product ID, fetch recommendations which includes the product itself
    const fetchPromises = productIds.map(productId => {
      const url = `/recommendations/products.json?product_id=${productId}&limit=10`;
      return fetch(url)
        .then(response => {
          if (!response.ok) {
            return null;
          }
          return response.json();
        })
        .then(data => {
          const products = data.products || [];
          // Find the product that matches our ID
          return products.find(p => p.id.toString() === productId.toString());
        })
        .catch(() => null);
    });

    return Promise.all(fetchPromises).then(products => {
      // Filter out null values (failed fetches) and sort to match original order
      return products.filter(p => p !== null && p !== undefined);
    }).catch(error => {
      console.error('Error fetching products:', error);
      return [];
    });
  }

  function fetchRandomProducts(count) {
    // Fetch products from the 'all' collection
    // Fetch more products to ensure we have enough for randomization
    const url = `/collections/all/products.json?limit=100`;

    return fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch random products');
        }
        return response.json();
      })
      .then(data => {
        const allProducts = data.products || [];

        // Randomly shuffle and select the requested count
        const shuffled = [...allProducts].sort(() => 0.5 - Math.random());
        return shuffled.slice(0, count);
      })
      .catch(error => {
        console.error('Error fetching random products:', error);
        return [];
      });
  }

  function renderProducts(products, container, emptyStateElement, section) {

    if (!products || products.length === 0) {
      container.innerHTML = '';
      // Hide the entire section when empty
      if (section) {
        section.style.display = 'none';
      }
      return;
    }

    // Show the section when products are available
    if (section) {
      section.style.display = '';
    }

    if (emptyStateElement) {
      emptyStateElement.style.display = 'none';
    }

    const productsHTML = products.map((product, index) =>
      createProductCard(product, index)
    ).join('');

    container.innerHTML = productsHTML;
  }

  function initRecentlyViewedProducts() {
    const section = document.getElementById('recently_viewed');
    if (!section) {
      return;
    }

    const productsContainer = section.querySelector('.collection-recently-viewed__products');
    const emptyStateElement = section.querySelector('.collection-recently-viewed__empty');

    if (!productsContainer) {
      return;
    }

    // Get products limit from section settings
    const productsLimit = parseInt(section.dataset.productsLimit || '12', 10);

    // Get product data from localStorage (viewedProducts format)
    let productIds = [];
    let productHandles = [];
    try {
      const viewedProductsData = localStorage.getItem('viewedProducts');
      if (viewedProductsData) {
        const parsed = JSON.parse(viewedProductsData);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Extract handles from URLs
          productHandles = parsed.map(item => {
            if (item.url) {
              const urlParts = item.url.split('/products/');
              if (urlParts.length > 1) {
                const handle = urlParts[1].split('?')[0].split('#')[0];
                return handle || null;
              }
            }
            return null;
          }).filter(h => h !== null && h !== '');

          // Extract product IDs as fallback
          productIds = parsed.map(item => {
            const id = item.productId || item.id;
            return typeof id === 'string' ? parseInt(id, 10) : id;
          }).filter(id => id && !isNaN(id));
        }
      }
    } catch (error) {
      console.error('Error reading localStorage:', error);
    }

    // Show loading state
    productsContainer.innerHTML = '<div style="padding: 40px; text-align: center;">Lade Produkte...</div>';

    // Check if we have any products to fetch
    if (productHandles.length === 0 && productIds.length === 0) {
      // No recently viewed products - hide section
      renderProducts([], productsContainer, emptyStateElement, section);
      return;
    }

    // Prefer handles over IDs (more reliable)
    if (productHandles.length > 0) {
      productHandles = productHandles.slice(0, productsLimit);
      fetchProductsByHandles(productHandles)
        .then(products => {
          // If handles returned no products, try IDs as fallback
          if (products.length === 0 && productIds.length > 0) {
            productIds = productIds.slice(0, productsLimit);
            return fetchProductsByIds(productIds);
          }
          return products;
        })
        .catch(error => {
          console.error('Error loading recently viewed products by handles:', error);
          // Try IDs as fallback if handles fail
          if (productIds.length > 0) {
            productIds = productIds.slice(0, productsLimit);
            return fetchProductsByIds(productIds);
          }
          return Promise.resolve([]);
        })
        .then(products => {
          renderProducts(products, productsContainer, emptyStateElement, section);
        })
        .catch(error => {
          console.error('Error loading recently viewed products:', error);
          renderProducts([], productsContainer, emptyStateElement, section);
        });
    } else {
      // Use IDs if no handles available
      productIds = productIds.slice(0, productsLimit);
      fetchProductsByIds(productIds)
        .then(products => {
          renderProducts(products, productsContainer, emptyStateElement, section);
        })
        .catch(error => {
          console.error('Error loading recently viewed products:', error);
          renderProducts([], productsContainer, emptyStateElement, section);
        });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRecentlyViewedProducts);
  } else {
    initRecentlyViewedProducts();
  }
})();
